import { Pagination, Layout, message } from 'antd';
import styled from 'styled-components';
import { TableEvenOdd } from '../account/styles';
import { ColumnsType, TableProps } from 'antd/es/table';
import { useEffect, useState } from 'react';
import Button, { BUTTON_VARIANT_TYPES } from '../../components/Button';
import EyeIcon from './components/EyeIcon';
import DeleteOutlinedIcon from './components/DeleteOutlinedIcon';
import notificationApis from '../../services/notificationApis';
import { setLoading } from '../../store/loading/actions';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IResponseTableData, IResponseTableDataBody } from '../../utils/response';
import { INotification } from './services/types';

const { Content } = Layout;

const PAGE_SIZE = 10;

const NotificationPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<IResponseTableData<INotification>>();

  const handleViewNotification = (id: string) => {
    navigate(`/notification/${id}`);
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const response = await notificationApis.deleteNotification(id);

      if (response?.data?.data) {
        message.success('Delete notification successful');
        getAllNotifications(currentPage);
      } else {
        message.error({ content: response.data.message as string, duration: 2 });
      }
    } catch (error) {
      message.error({ content: 'Delete notification failed', duration: 2 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onPageChanged = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const columns: ColumnsType<INotification> = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (title: string) => <TitleLinkStyled>{title}</TitleLinkStyled>,
    },
    {
      title: 'Content',
      dataIndex: 'content',
      width: 400,
      ellipsis: true,
    },
    {
      title: 'Sent List',
      dataIndex: 'sendType',
    },
    {
      title: 'Type',
      dataIndex: 'contentType',
    },
    {
      title: 'Sent Date',
      dataIndex: 'createdDate',
    },
    {
      title: 'Actions',
      width: '120px',
      dataIndex: 'id',
      render: (id: string) => (
        <ActionButtonsStyled>
          <ActionButtonStyled onClick={() => handleViewNotification(id)}>
            <EyeIcon />
          </ActionButtonStyled>
          <ActionButtonStyled onClick={() => handleDeleteNotification(id)} className='btn-delete'>
            <DeleteOutlinedIcon />
          </ActionButtonStyled>
        </ActionButtonsStyled>
      ),
    },
  ];

  const handleAddNotification = () => {
    navigate('/notification/create');
  };

  const getAllNotifications = async (page: number) => {
    try {
      dispatch(setLoading(true));
      const response: IResponseTableDataBody<INotification> = await notificationApis.getAllNotifications(
        page,
        PAGE_SIZE
      );
      if (response?.data?.data?.content) {
        setData(response.data.data);
      }
    } catch (error) {
      message.error('Failed to get notifications');
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    getAllNotifications(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <ContainerStyled>
      <HeaderStyled>
        <TitleStyled>Notifications</TitleStyled>
        <Button icon={'add'} variant={BUTTON_VARIANT_TYPES.GREEN} onClick={handleAddNotification}>
          Add Notification
        </Button>
      </HeaderStyled>

      <TableContentStyled columns={columns} dataSource={data?.content} rowKey='id' bordered pagination={false} />

      <PagingStyled>
        <Pagination
          current={currentPage}
          total={data?.totalElements}
          pageSize={PAGE_SIZE}
          onChange={onPageChanged}
          showSizeChanger={false}
        />
      </PagingStyled>
    </ContainerStyled>
  );
};

export default NotificationPage;

const ContainerStyled = styled.div`
  height: calc(100vh - 129px);
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f5f5f5;
`;

const HeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleStyled = styled.div`
  color: #1d2129;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const TableContentStyled = styled(TableEvenOdd)<TableProps<INotification>>`
  && .ant-table-thead > tr > th {
    background: #37a0bc;
    color: white;
    font-weight: 500;
    font-size: 16px;
    padding: 16px;
  }

  && .ant-table-tbody > tr > td {
    padding: 12px 16px;
    font-family: Poppins;
    font-size: 14px;
  }
`;

const TitleLinkStyled = styled.span`
  color: #008bff;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ActionButtonsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const ActionButtonStyled = styled.button`
  cursor: pointer;
  display: flex;
  width: 32px;
  height: 32px;
  min-width: 32px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  padding: 0;
  background: #008bff;
  &.btn-delete {
    background: #f53f3f;
  }
  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionDeleteButtonStyled = styled(Button)`
  cursor: pointer;
  display: flex;
  width: 32px;
  height: 32px;
  min-width: 32px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  padding: 0;
  background: #f53f3f;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PagingStyled = styled(Content)`
  text-align: right;
  margin-top: 10px;
  margin-bottom: 10px;
`;
